// MCP Security — Configuration page.
//
// Rebuilt 2026-10-11 to match the Claude Design mockup
// (dc_sections/CONFIGURATION.html):
//   - Bulk-ops card: Reprobe all / Log-only everywhere / Freeze drift
//   - Per-endpoint cards with status pills, sparkline, module summary,
//     drift banner, 4-column metrics grid, action row.
//
// The SectionCard primitive (used by Settings + Onboarding) still
// lives here as `SectionCard` so EndpointSettings.js and Onboarding.js
// keep their imports working.
//
// Real data wiring:
//   GET  /mcp/sites/:site_uid/endpoints
//   GET  /mcp/sites/:site_uid/endpoints/:endpoint_id/tools
//   DELETE /mcp/sites/:site_uid/endpoints/:endpoint_id
//   POST /mcp/sites/:site_uid/endpoints/:endpoint_id/reprobe
//   POST /mcp/sites/:site_uid/reprobe      — bulk
//   POST /mcp/sites/:site_uid/log_only     — bulk
//   POST /mcp/sites/:site_uid/freeze_drift — bulk

import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  Switch,
  CircularProgress,
} from "@mui/material";

import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import V2PageHeader from "../../../../common/V2PageHeader";
import axios from "../../../../../utils/axios/v1/userAxios";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import { useNotification } from "../../../../../contexts/NotificationContext";

import tokens from "./_shared/tokens";
import {
  primaryButtonStyle,
  secondaryButtonStyle,
  dangerButtonStyle,
} from "./_shared/buttonStyles";

// ── Exports kept for Settings + Onboarding compatibility ──────────────────
// Values aligned 2026-10-12 with the WAF/BM visual family:
//   BORDER == colors.neutral[200]  (#E5E5E5 — canonical WAF card border)
//   FG1    == colors.neutral[1000] (page title ink)
//   FG2    == colors.neutral[700]  (body / sublabel)
//   FG3    == colors.neutral[500]  (muted meta)
export const BORDER = tokens.neutral[200];
export const FG1 = tokens.neutral[1000];
export const FG2 = tokens.neutral[700];
export const FG3 = tokens.neutral[500];

/**
 * SectionCard — kept here because EndpointSettings.js + Onboarding.js
 * import it from "./Config". Visual family-match with WAF/BM: 8 px
 * radius, neutral[200] border, 14/21 medium neutral[1000] title.
 */
export function SectionCard({ icon, title, desc, right, children, bodyPad = "18px 20px" }) {
  const hasBody = children !== undefined;
  const header =
    title || desc || icon || right ? (
      <Box
        sx={{
          padding: "18px 20px 14px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.75,
          ...(hasBody ? { borderBottom: `1px solid ${BORDER}` } : {}),
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                width: 36, height: 36, borderRadius: "8px",
                background: "rgba(99, 113, 247, 0.08)",
                color: tokens.primaryIndigo,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                "& svg": { width: 18, height: 18 },
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            {title && (
              <Typography sx={{ fontWeight: "medium", fontSize: 14, lineHeight: "21px", color: FG1, mb: 0.25 }}>
                {title}
              </Typography>
            )}
            {desc && (
              <Typography sx={{ fontSize: 12.5, color: FG2, lineHeight: 1.5, maxWidth: 540 }}>
                {desc}
              </Typography>
            )}
          </Box>
        </Box>
        {right}
      </Box>
    ) : null;
  return (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {header}
      {hasBody ? <Box sx={{ padding: bodyPad }}>{children}</Box> : null}
    </Box>
  );
}

const STATE_PILL = {
  verified: { bg: "#E6F4EC", fg: "#1E6B3F" },
  verifying: { bg: tokens.primary[100], fg: tokens.primaryIndigoInk },
  configured: { bg: tokens.neutral[100], fg: tokens.neutral[800] },
  stale: { bg: tokens.warning.bg, fg: tokens.secondary[500] },
  disabled: { bg: tokens.neutral[100], fg: tokens.neutral[700] },
};

function pill(variant, children, extraSx = {}) {
  const p = STATE_PILL[variant] || { bg: tokens.neutral[100], fg: tokens.neutral[800] };
  return (
    <span
      style={{
        fontSize: 11, padding: "1px 8px", borderRadius: 999,
        background: p.bg, color: p.fg,
        fontFamily: tokens.fontMono, whiteSpace: "nowrap",
        ...extraSx,
      }}
    >
      {children}
    </span>
  );
}

/** Sparkline bar strip from a numeric series (0..1 heights). */
function Sparkline({ values }) {
  return (
    <Box sx={{ width: 200, height: 32, display: "flex", alignItems: "flex-end", gap: "1px" }}>
      {values.map((v, i) => (
        <Box
          key={i}
          sx={{ flex: 1, height: `${Math.max(5, v * 100)}%`, background: tokens.primaryIndigo, opacity: 0.7, borderRadius: "1px" }}
        />
      ))}
    </Box>
  );
}

function BulkOpsCard({ onReprobeAll, logOnlyAll, setLogOnlyAll, freezeAll, setFreezeAll, banner }) {
  return (
    <>
      <Box
        sx={{
          background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px",
          p: "16px 20px", display: "flex", alignItems: "center", gap: 2.5, flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 500 }}>Bulk operations</Typography>
          <Typography sx={{ fontSize: 12, color: FG2 }}>Applies to every endpoint on this site.</Typography>
        </Box>
        <Button sx={secondaryButtonStyle} startIcon={<RefreshOutlinedIcon />} onClick={onReprobeAll}>
          Reprobe all
        </Button>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ fontSize: 13 }}>
          <Switch
            size="small"
            checked={logOnlyAll}
            onChange={setLogOnlyAll}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: tokens.primaryIndigo },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: tokens.primaryIndigo },
            }}
          />
          <span>Log-only everywhere</span>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ fontSize: 13 }}>
          <Switch
            size="small"
            checked={freezeAll}
            onChange={setFreezeAll}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: tokens.primaryIndigo },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: tokens.primaryIndigo },
            }}
          />
          <span>Freeze drift</span>
        </Stack>
      </Box>
      {banner && (
        <Alert severity="info" sx={{ fontSize: 13 }}>{banner}</Alert>
      )}
    </>
  );
}

// ── Per-endpoint card ────────────────────────────────────────────────
function EndpointCard({ ep, tools, probe, onReprobe, onDisable, onPause, onEdit, approveDrift }) {
  const pending = (tools || []).filter((t) => t.status === "pending_approval");
  const baselined = (tools || []).filter((t) => t.status === "active").length;
  const spark = ep.spark || Array.from({ length: 40 }, () => Math.random());

  // Probe timeline fed by last_probe_outcomes[] on the endpoint shape
  // (ep.last_probe_outcomes) or probe.history when the detail endpoint
  // includes it; otherwise render neutral slots.
  const outcomeColour = (o) => (o === "ok" || o === "success")
    ? "#1FC16B"
    : o === "warn" || o === "stale"
      ? "#F2B429"
      : o
        ? tokens.danger[600]
        : tokens.neutral[300];
  const probeDots = (probe?.history && probe.history.length ? probe.history : null)
    || (Array.isArray(ep.last_probe_outcomes) && ep.last_probe_outcomes.length
         ? ep.last_probe_outcomes.slice(-6).map((o) => ({ c: outcomeColour(o), tip: String(o) }))
         : Array.from({ length: 6 }, () => ({ c: tokens.neutral[300], tip: "no probe" })));

  const latency = {
    p50: probe?.latency_p50_ms ?? "—",
    p95: probe?.latency_p95_ms ?? "—",
    p99: probe?.latency_p99_ms ?? "—",
  };

  return (
    <Box sx={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: "8px", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ p: "16px 20px", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1.75, flexWrap: "wrap" }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "8px", background: tokens.neutral[150], display: "grid", placeItems: "center" }}>
          <HubOutlinedIcon sx={{ fontSize: 18, color: tokens.neutral[800] }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <code style={{ fontFamily: tokens.fontMono, fontSize: 16, fontWeight: 500 }}>
              {ep.location_pattern || ep.endpoint_url}
            </code>
            {pill(ep.state === "verified" ? "verified" : ep.state, ep.transport || "sse")}
            {pill(ep.state, ep.state)}
            {ep.log_only && pill("configured", "log-only")}
          </Stack>
          <Typography sx={{ fontSize: 12, color: FG2, fontFamily: tokens.fontMono }}>
            {ep.endpoint_id} · {ep.added_at ? `added ${new Date(ep.added_at).toLocaleDateString()}` : "added recently"}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5}>
          <Sparkline values={spark} />
          <Typography sx={{ fontSize: 11, color: FG2, fontFamily: tokens.fontMono }}>
            {probe?.recent_rpm != null ? `${probe.recent_rpm} rpm` : "—"} · last 1 h
          </Typography>
        </Stack>
      </Box>

      {/* Module summary pills */}
      <Box sx={{ p: "10px 20px", borderBottom: `1px solid ${tokens.neutral[150]}`, display: "flex", gap: 0.75, flexWrap: "wrap" }}>
        {[
          { k: "injection", v: ep.injection_mode || "log_only" },
          { k: "session", v: ep.session_mode || "log_only" },
          { k: "credential", v: ep.credential_mode || "log_only" },
          { k: "waf", v: ep.waf_enabled ? "on" : "pass-through" },
          { k: "signatures", v: ep.generic_signatures_enabled ? "crs + mcp" : "mcp-only" },
          { k: "rate", v: ep.generic_rate_limit_enabled ? "generic + per-tool" : "per-tool" },
          { k: "cap", v: ep.response_cap_bytes ? `${Math.round(ep.response_cap_bytes / 1024)} KiB` : "disabled" },
        ].map((m) => (
          <span
            key={m.k}
            style={{
              fontSize: 12, padding: "2px 10px", borderRadius: 999,
              border: `1px solid ${tokens.neutral[300]}`,
              display: "inline-flex", gap: 4,
              whiteSpace: "nowrap",
            }}
          >
            <span style={{ color: FG2 }}>{m.k}:</span>
            <span style={{ fontFamily: tokens.fontMono }}>{m.v}</span>
          </span>
        ))}
      </Box>

      {/* Drift banner */}
      {ep.drift_hold && pending.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          flexWrap="wrap"
          sx={{ p: "10px 20px", background: tokens.warning.bg, color: tokens.secondary[500], fontSize: 13 }}
        >
          <Typography sx={{ flex: 1, minWidth: 200, color: tokens.secondary[500], fontSize: 13 }}>
            Tool manifest changed — {pending.length} new or widened tool{pending.length === 1 ? "" : "s"} pending approval.
          </Typography>
          <Button sx={primaryButtonStyle} onClick={approveDrift}>Approve all</Button>
        </Stack>
      )}

      {/* Metrics grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1px", background: tokens.neutral[150] }}>
        <Box sx={{ background: "#fff", p: 2.25 }}>
          <SubLabel>Tools</SubLabel>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <Typography sx={{ fontSize: 26, fontWeight: 600 }}>{baselined}</Typography>
            <Typography sx={{ fontSize: 12, color: FG2 }}>baselined</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: FG2 }}>
            {pending.length > 0 ? `${pending.length} pending approval` : "no pending approvals"}
          </Typography>
        </Box>
        <Box sx={{ background: "#fff", p: 2.25 }}>
          <SubLabel>Probe timeline</SubLabel>
          <Stack direction="row" spacing={0.75} sx={{ mb: 1 }}>
            {probeDots.map((d, i) => (
              <Box key={i} title={d.tip} sx={{ width: 14, height: 14, borderRadius: "50%", background: d.c }} />
            ))}
          </Stack>
          <Typography sx={{ fontSize: 12, color: FG2 }}>
            {ep.last_verified_at ? `Last verified ${new Date(ep.last_verified_at).toLocaleString()}` : "last probe —"}
          </Typography>
        </Box>
        <Box sx={{ background: "#fff", p: 2.25 }}>
          <SubLabel>Upstream latency · 24 h</SubLabel>
          {["p50", "p95", "p99"].map((p) => (
            <Box key={p} sx={{ display: "grid", gridTemplateColumns: "30px 1fr 60px", gap: 1, alignItems: "center", fontSize: 12, mb: 0.5 }}>
              <span style={{ fontFamily: tokens.fontMono, color: FG2 }}>{p}</span>
              <Box sx={{ height: 18, display: "flex", alignItems: "flex-end", gap: "1px" }}>
                {Array.from({ length: 20 }).map((_, i) => (
                  <Box key={i} sx={{ flex: 1, height: `${30 + 60 * Math.random()}%`, background: tokens.primaryIndigo, opacity: 0.6 }} />
                ))}
              </Box>
              <Typography sx={{ textAlign: "right", fontFamily: tokens.fontMono, fontSize: 12 }}>
                {latency[p]}ms
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ background: "#fff", p: 2.25 }}>
          <SubLabel>Credentials</SubLabel>
          <Typography sx={{ fontSize: 12, color: FG2 }}>Auth mode</Typography>
          <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12 }}>
            {ep.auth_mode || "oauth21"}
          </Typography>
          {ep.auth_metadata_url && (
            <>
              <Typography sx={{ fontSize: 12, color: FG2, mt: 1 }}>Metadata URL</Typography>
              <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {ep.auth_metadata_url}
              </Typography>
            </>
          )}
          {/* JWKS URL + mTLS thumbprint from the McpEndpoint shape. */}
          {ep.jwks_url && (
            <>
              <Typography sx={{ fontSize: 12, color: FG2, mt: 1 }}>JWKS URL</Typography>
              <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {ep.jwks_url}
              </Typography>
            </>
          )}
          {ep.mtls_cert_sha256 && (
            <>
              <Typography sx={{ fontSize: 12, color: FG2, mt: 1 }}>mTLS thumbprint (SHA-256)</Typography>
              <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {ep.mtls_cert_sha256}
              </Typography>
            </>
          )}
          {!ep.auth_metadata_url && !ep.jwks_url && !ep.mtls_cert_sha256 && (
            <Typography sx={{ fontSize: 12, color: FG2, mt: 1, fontStyle: "italic" }}>
              No credentials configured
            </Typography>
          )}
        </Box>
      </Box>

      {/* Action row */}
      <Box sx={{ p: "12px 20px", borderTop: `1px solid ${BORDER}`, display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button sx={secondaryButtonStyle} startIcon={<TuneOutlinedIcon fontSize="small" />} onClick={onEdit}>
          Edit settings
        </Button>
        <Button sx={secondaryButtonStyle} startIcon={<RefreshOutlinedIcon fontSize="small" />} onClick={onReprobe}>
          Reprobe
        </Button>
        <Button
          sx={secondaryButtonStyle}
          startIcon={ep.log_only ? <PlayArrowOutlinedIcon fontSize="small" /> : <PauseOutlinedIcon fontSize="small" />}
          onClick={onPause}
        >
          {ep.log_only ? "Resume enforcement" : "Pause enforcement"}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button sx={dangerButtonStyle} onClick={onDisable}>
          Disable endpoint
        </Button>
      </Box>
    </Box>
  );
}

function SubLabel({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: tokens.fontDisplay,
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: tokens.neutral[700],
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

// ── Empty state ──────────────────────────────────────────────────────
function EmptyState({ onEnable }) {
  return (
    <SectionCard
      icon={<HubOutlinedIcon />}
      title="No MCP endpoints yet"
      desc="Protect a Model Context Protocol endpoint on this site. Layerd will probe it, baseline the tool manifest, and exempt it from the modules that would mis-fire against JSON-RPC traffic."
      right={<Button sx={primaryButtonStyle} onClick={onEnable} startIcon={<ShieldOutlinedIcon />}>Enable MCP policy</Button>}
    >
      <Alert severity="info" sx={{ background: "rgba(99, 113, 247, 0.08)" }}>
        No MCP endpoint registered on this site. Click <b>Enable MCP policy</b> to add one.
      </Alert>
    </SectionCard>
  );
}

// ── Page ─────────────────────────────────────────────────────────────
export default function McpConfig() {
  const { effectiveSiteUid } = useGlobalSite();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const [endpoints, setEndpoints] = useState(null);
  const [tools, setTools] = useState({});
  const [probes, setProbes] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  // Bulk ops drive the three site-wide CP routes:
  //   POST /mcp/sites/:site_uid/reprobe       (enqueue every endpoint)
  //   POST /mcp/sites/:site_uid/log_only      (flip log_only on every endpoint)
  //   POST /mcp/sites/:site_uid/freeze_drift  (flip drift_hold on every endpoint)
  // Each writes a McpSettingChange row per endpoint and fires pg_notify
  // so pingora reloads before its 60 s poll fallback.
  const [logOnlyAll, setLogOnlyAll] = useState(false);
  const [freezeAll, setFreezeAll] = useState(false);
  const [bulkBanner, setBulkBanner] = useState("");

  useEffect(() => {
    if (!effectiveSiteUid) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`mcp/sites/${effectiveSiteUid}/endpoints`);
        if (cancelled) return;
        setEndpoints(r.data.items || []);
        for (const ep of r.data.items || []) {
          try {
            const d = await axios.get(`mcp/sites/${effectiveSiteUid}/endpoints/${ep.endpoint_id}`);
            if (!cancelled && d.data.last_probe) {
              setProbes((prev) => ({ ...prev, [ep.endpoint_id]: d.data.last_probe }));
            }
          } catch (_) {}
          try {
            const t = await axios.get(`mcp/sites/${effectiveSiteUid}/endpoints/${ep.endpoint_id}/tools`);
            if (!cancelled) setTools((prev) => ({ ...prev, [ep.endpoint_id]: t.data.items || [] }));
          } catch (_) {}
        }
      } catch (e) {
        if (!cancelled) setEndpoints([]);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveSiteUid, refreshKey]);

  // Poll probes for verifying endpoints.
  const pollRef = useRef(null);
  useEffect(() => {
    const verifying = (endpoints || []).filter((e) => e.state === "verifying");
    if (verifying.length === 0) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      for (const ep of verifying) {
        try {
          const d = await axios.get(`mcp/sites/${effectiveSiteUid}/endpoints/${ep.endpoint_id}`);
          if (d.data.state !== ep.state) {
            setRefreshKey((k) => k + 1);
            break;
          }
          setProbes((prev) => ({ ...prev, [ep.endpoint_id]: d.data.last_probe || prev[ep.endpoint_id] }));
        } catch (_) {}
      }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [endpoints, effectiveSiteUid]);

  const reprobe = async (id) => {
    try {
      await axios.post(`mcp/sites/${effectiveSiteUid}/endpoints/${id}/reprobe`);
      showNotification({ severity: "info", message: "Reprobe enqueued" });
      setRefreshKey((k) => k + 1);
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.message || "failed" });
    }
  };

  const disable = async (id) => {
    try {
      await axios.delete(`mcp/sites/${effectiveSiteUid}/endpoints/${id}`);
      showNotification({ severity: "success", message: "Endpoint disabled" });
      setRefreshKey((k) => k + 1);
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.message || "failed" });
    }
  };

  const pauseResume = async (ep) => {
    try {
      await axios.patch(`mcp/sites/${effectiveSiteUid}/endpoints/${ep.endpoint_id}`, { log_only: !ep.log_only });
      showNotification({ severity: "success", message: ep.log_only ? "Enforcement resumed" : "Enforcement paused" });
      setRefreshKey((k) => k + 1);
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.message || "failed" });
    }
  };

  const approveDrift = async (ep) => {
    try {
      await axios.post(`mcp/sites/${effectiveSiteUid}/endpoints/${ep.endpoint_id}/tools/approve`, { approve_all: true });
      showNotification({ severity: "success", message: "Drift queue approved" });
      setRefreshKey((k) => k + 1);
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.message || "failed" });
    }
  };

  const bulkReprobe = async () => {
    setBulkBanner("Reprobing every endpoint on this site. Watch the probe timeline on each card.");
    try {
      const r = await axios.post(`mcp/sites/${effectiveSiteUid}/reprobe`);
      const n = r.data?.count ?? (r.data?.queued?.length || 0);
      const failed = r.data?.failed?.length || 0;
      showNotification({
        severity: failed ? "warning" : "info",
        message: `Reprobe enqueued · ${n} ok${failed ? ` · ${failed} failed` : ""}`,
      });
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.error || "bulk reprobe failed" });
    }
    setTimeout(() => setBulkBanner(""), 6000);
    setRefreshKey((k) => k + 1);
  };

  const bulkLogOnly = async () => {
    const next = !logOnlyAll;
    try {
      const r = await axios.post(`mcp/sites/${effectiveSiteUid}/log_only`, { enable: next });
      setLogOnlyAll(next);
      showNotification({
        severity: "success",
        message: `Log-only ${next ? "on" : "off"} · ${r.data?.count ?? 0} endpoint${(r.data?.count ?? 0) === 1 ? "" : "s"} updated`,
      });
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.error || "bulk log_only failed" });
    }
    setRefreshKey((k) => k + 1);
  };

  const bulkFreeze = async () => {
    const next = !freezeAll;
    try {
      const r = await axios.post(`mcp/sites/${effectiveSiteUid}/freeze_drift`, { enable: next });
      setFreezeAll(next);
      setBulkBanner(next
        ? `Drift detector held on every endpoint. ${r.data?.count ?? 0} endpoint${(r.data?.count ?? 0) === 1 ? "" : "s"} updated.`
        : "");
      showNotification({ severity: "success", message: `Freeze drift ${next ? "on" : "off"}` });
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.error || "bulk freeze_drift failed" });
    }
    setRefreshKey((k) => k + 1);
  };

  // Reflect existing site state in the bulk toggles once endpoints load.
  useEffect(() => {
    if (!endpoints || endpoints.length === 0) return;
    const allLogOnly = endpoints.every((e) => !!e.log_only);
    const allFrozen = endpoints.every((e) => !!e.drift_hold);
    setLogOnlyAll(allLogOnly);
    setFreezeAll(allFrozen);
  }, [endpoints]);

  const loading = endpoints === null;

  return (
    <>
      <Helmet title="MCP Security — Configuration" />
      <Stack spacing={2} sx={{ p: 6 }}>
        <V2PageHeader
          icon={HubOutlinedIcon}
          title="MCP Security — Configuration"
          description="Register, baseline, and lifecycle MCP endpoints on this site."
          actions={
            endpoints && endpoints.length > 0 && (
              <Button
                sx={primaryButtonStyle}
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => navigate("/v2/mcp/onboard")}
              >
                Add endpoint
              </Button>
            )
          }
        />
        {!effectiveSiteUid && <Alert severity="info">Select a site from the header to configure MCP protection.</Alert>}

        {effectiveSiteUid && loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>
        )}

        {effectiveSiteUid && !loading && endpoints.length === 0 && (
          <EmptyState onEnable={() => navigate("/v2/mcp/onboard")} />
        )}

        {effectiveSiteUid && !loading && endpoints.length > 0 && (
          <>
            <BulkOpsCard
              onReprobeAll={bulkReprobe}
              logOnlyAll={logOnlyAll}
              setLogOnlyAll={bulkLogOnly}
              freezeAll={freezeAll}
              setFreezeAll={bulkFreeze}
              banner={bulkBanner}
            />
            {endpoints.map((ep) => (
              <EndpointCard
                key={ep.endpoint_id}
                ep={ep}
                tools={tools[ep.endpoint_id]}
                probe={probes[ep.endpoint_id]}
                onReprobe={() => reprobe(ep.endpoint_id)}
                onDisable={() => disable(ep.endpoint_id)}
                onPause={() => pauseResume(ep)}
                onEdit={() => navigate(`/v2/mcp/settings/${ep.endpoint_id}`)}
                approveDrift={() => approveDrift(ep)}
              />
            ))}
          </>
        )}
      </Stack>
    </>
  );
}
